import {computed, effect, inject, Injectable, OnDestroy, signal} from "@angular/core"
import {DataObjectWithRelated, fetchOk} from "@cm/utils"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_RENDERING} from "@common/services/logging"
import {assembleTiles, BrowserWebSocketImplementation, createWebSocketMessagingConnection} from "@cm/browser-utils"
import {connectRenderEngineMessaging, RenderEngineMessagingConnection, RendererImage, RenderNodes, renderWithTiles, TileUploadState} from "@cm/render-nodes"
import {buildRenderGraph, SceneNodes, TextureResolution} from "@cm/template-nodes"
import {contentTypeForFilenameOrNull} from "@cm/utils/content-types"
import {TypedImageData} from "@cm/utils/typed-image-data"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {GetDataObjectDetailsGQL, HdriForRenderingGQL, RenderingDataObjectFragment} from "@common/services/rendering/rendering.generated"
import {LocalStorageService} from "@app/common/services/local-storage/local-storage.service"
import {DataResolverService} from "@app/material-editor/services/data-resolver.service"
import {MaterialGraphCyclesReturns, resolveTextureResolution} from "@cm/material-nodes"
import {Vector3} from "@cm/math"
import {ImageProcessingService} from "@common/services/rendering/image-processing.service"
import {TextureTileLoaderService} from "@common/services/texture-tile-loader/texture-tile-loader.service"
import {firstValueFrom} from "rxjs"
import {ImageDataObjectInfo, ImageDataObjectThumbnailTriggerService} from "@common/services/generate-thumbnail/image-data-object-thumbnail-trigger.service"

const STANDALONE_RENDER_ENGINE_URL = "ws://127.0.0.1:17093" // cmRenderEngine
const INTERACTIVE_RENDER_SERVER_URL = "ws://127.0.0.1:17095" // colormass Render Server app
const UPDATE_INTERVAL_MS = 500
const CONNECT_INTERVAL_MS = 3000
const USE_GPU_DEFAULT = true
const TEXTURE_RESOLUTION_DEFAULT: TextureResolution = "original"
const TEXTURE_STREAMING_OVERRIDE_DEFAULT: TextureStreaming = "from-render-node"
const MAX_MESSAGE_SIZE_BYTES = 1024 * 1024 * 1024

type LegacyId = number

export type TextureStreaming = "from-render-node" | "on" | "off"

type PreviewConfig = {
    fullQuality: boolean
    useGPU: boolean
    textureResolution: TextureResolution
    textureStreaming: TextureStreaming
    width: number
    height: number
    samples: number
    pass?: RenderNodes.PassName
}

class PromiseCache<K, V> extends Map<K, Promise<V>> {
    constructor() {
        super()
    }

    getOrResolve(key: K, fn: (key: K) => Promise<V>): Promise<V> {
        let promise = super.get(key)
        if (!promise) {
            promise = fn(key)
            super.set(key, promise)
        }
        return promise
    }
}

function computeGraphHash(_graph: RenderNodes.Render): string | undefined {
    // return hashObject(graph)
    return undefined
}

function rendererImageToTypedImageData(image: RendererImage): TypedImageData {
    return {
        data: image.data,
        width: image.width,
        height: image.height,
        channelLayout: (() => {
            switch (image.components) {
                case 1:
                    return "L"
                case 3:
                    return "RGB"
                case 4:
                    return "RGBA"
                default:
                    throw Error("Unsupported number of components")
            }
        })(),
        dataType: image.dataType,
        colorSpace: "linear",
    }
}

function resolveDataObjectContentType(dataObject: {id: string; originalFileName: string; mediaType?: string | null}): string {
    const contentType = (dataObject.originalFileName ? contentTypeForFilenameOrNull(dataObject.originalFileName) : null) ?? dataObject.mediaType
    if (!contentType) throw Error(`Could not resolve content type for DataObject ${dataObject.id}`)
    return contentType
}

@Injectable()
export class LocalPreviewRenderingService implements OnDestroy {
    private readonly $renderer = signal<RenderEngineMessagingConnection<BrowserWebSocketImplementation> | null>(null)
    private lastProgress: number | undefined = undefined
    private timerId!: ReturnType<typeof setInterval>
    private dataObjectCache = new PromiseCache<LegacyId, RenderingDataObjectFragment>()
    private dataObjectSendCache = new PromiseCache<LegacyId, void>()
    private tileUploadState = new TileUploadState()
    private lastGraphHash: string | undefined = undefined
    private lastSubmittedNodes: SceneNodes.SceneNode[] | undefined = undefined
    private lastSubmittedCamera: SceneNodes.Camera | undefined = undefined
    private lastSubmittedConfigKey: string | undefined = undefined
    private readonly $_numPendingTasks = signal<number>(0)
    private state:
        | "Disconnected"
        | "Connecting"
        | "Idle"
        | "Building"
        | "FetchingData"
        | "DeterminingTextureTiles"
        | "AssemblingTextures"
        | "Updating"
        | "FetchingStatus"
        | "FetchingPreview"
        | "Disconnecting" = "Disconnected"
    private updatePending = false
    private lastConnectionAttempt: Date | undefined = undefined
    private disconnectRequested = false

    // inputs signals:
    readonly $enable = signal<boolean>(false)
    readonly $ambientLight = signal<boolean>(true)
    readonly $sceneNodes = signal<SceneNodes.SceneNode[] | undefined>(undefined)
    readonly $cameraOverride = signal<SceneNodes.Camera | undefined>(undefined)
    readonly $fullQuality = signal<boolean>(false)
    readonly $useGPU = signal<boolean>(USE_GPU_DEFAULT)
    readonly $textureResolution = signal<TextureResolution>(TEXTURE_RESOLUTION_DEFAULT)
    readonly $textureStreaming = signal<TextureStreaming>(TEXTURE_STREAMING_OVERRIDE_DEFAULT)
    readonly $overlayHelpers = signal<boolean>(true)
    readonly $size = signal<[number, number] | undefined>(undefined)
    readonly $samples = signal<number>(128)
    readonly $pass = signal<RenderNodes.PassName>("Combined")

    // output signals:
    readonly $statusMessage = signal<string>("")
    readonly $errorMessage = signal<string | null>(null)
    readonly $progress = signal<number | null>(null)
    readonly $previewImageData = signal<TypedImageData | null>(null)
    readonly $connected = signal<boolean>(false)
    $numPendingTasks = this.$_numPendingTasks.asReadonly()

    readonly availablePasses: {name: string; value: RenderNodes.PassName}[] = [
        {name: "Combined", value: "Combined"},
        {name: "Diffuse Direct", value: "DiffuseDirect"},
        {name: "Diffuse Indirect", value: "DiffuseIndirect"},
        {name: "Diffuse Color", value: "DiffuseColor"},
        {name: "Glossy Direct", value: "GlossyDirect"},
        {name: "Glossy Indirect", value: "GlossyIndirect"},
        {name: "Glossy Color", value: "GlossyColor"},
        {name: "Transmission Direct", value: "TransmissionDirect"},
        {name: "Transmission Indirect", value: "TransmissionIndirect"},
        {name: "Transmission Color", value: "TransmissionColor"},
        {name: "Emission", value: "Emission"},
    ]

    readonly availableTextureResolutions: {name: string; value: TextureResolution}[] = [
        {name: "500 px", value: "500px"},
        {name: "1000 px", value: "1000px"},
        {name: "2000 px", value: "2000px"},
        {name: "Original", value: "original"},
    ]

    readonly availableTextureStreamingOverrides: {name: string; value: TextureStreaming}[] = [
        {name: "as Render node", value: "from-render-node"},
        {name: "On", value: "on"},
        {name: "Off", value: "off"},
    ]

    private readonly $config = computed(() => {
        const size = this.$size()
        if (!size) return undefined
        const config: PreviewConfig = {
            fullQuality: this.$fullQuality(),
            useGPU: this.$useGPU(),
            textureResolution: this.$textureResolution(),
            textureStreaming: this.$textureStreaming(),
            width: size[0],
            height: size[1],
            samples: this.$samples(),
            pass: this.$pass(),
        }
        return config
    })

    readonly getDataObjectDetails = inject(GetDataObjectDetailsGQL)
    readonly hdriForRendering = inject(HdriForRenderingGQL)
    private readonly localStorageService = inject(LocalStorageService)
    private readonly dataResolver = inject(DataResolverService)
    private readonly imageProcessingService = inject(ImageProcessingService)
    private readonly textureTileLoaderService = inject(TextureTileLoaderService)
    private readonly logger: CmLogger = inject(CM_LOGGER_RENDERING)
    private readonly imageDataObjectThumbnailTrigger = inject(ImageDataObjectThumbnailTriggerService)

    constructor() {
        effect(() => {
            if (this.$enable()) {
                this.$sceneNodes() // dependency
                this.$config() // dependency
                this.$cameraOverride() // dependency
                this.update()
            }
        })

        this.timerId = setInterval(() => this.onUpdateTimer(), UPDATE_INTERVAL_MS)

        effect(() => {
            this.logger.debug("Renderer status:", this.$statusMessage())
        })
        // effect(() => {
        //     console.log("Progress:", this.$progress())
        // })
    }

    ngOnDestroy() {
        clearInterval(this.timerId)
        this.disconnect()
    }

    private changeState(newState: typeof LocalPreviewRenderingService.prototype.state) {
        // console.log("Changed state from", this.state, "to", newState)
        this.state = newState
        switch (newState) {
            case "Disconnected":
            case "Connecting":
            case "Disconnecting":
                this.$connected.set(false)
                break
            default:
                this.$connected.set(true)
                break
        }
    }

    private connect() {
        switch (this.state) {
            case "Disconnected":
                break
            default:
                return
        }
        this.lastConnectionAttempt = new Date()
        this.disconnectRequested = false
        this.changeState("Connecting")
        const url = this.localStorageService.developmentMode ? STANDALONE_RENDER_ENGINE_URL : INTERACTIVE_RENDER_SERVER_URL
        connectRenderEngineMessaging(createWebSocketMessagingConnection, this.logger)(url, MAX_MESSAGE_SIZE_BYTES).then(
            (renderer) => {
                if (this.state !== "Connecting") {
                    renderer.disconnect()
                    this.changeState("Disconnected")
                    return
                }
                renderer.close$.then(() => {
                    // console.log("Renderer closed")
                    if (!this.disconnectRequested) {
                        this.setError("Lost connection to the render server (it may have crashed or been closed)")
                    }
                    this.changeState("Disconnected")
                    this.$statusMessage.set("")
                    this.lastProgress = undefined
                    this.$progress.set(null)
                    this.$previewImageData.set(null)
                    this.$renderer.set(null)
                    this.dataObjectSendCache.clear()
                    this.tileUploadState.clear()
                    this.lastConnectionAttempt = new Date()
                })
                this.$renderer.set(renderer)
                this.$statusMessage.set("")
                this.$progress.set(null)
                this.lastProgress = undefined
                this.$previewImageData.set(null)
                this.dataObjectSendCache.clear()
                this.tileUploadState.clear()
                this.updatePending = true
                this.checkForPendingUpdateOrReturnToIdle()
            },
            (_err) => {
                this.changeState("Disconnected")
                // console.error("Failed to connect to renderer:", err)
            },
        )
    }

    private disconnect() {
        if (this.state === "Disconnected") return
        this.disconnectRequested = true
        this.$errorMessage.set(null)
        this.changeState("Disconnecting")
        this.$renderer()?.disconnect()
    }

    private setError(message: string) {
        if (this.$errorMessage() === message) return // avoid spamming the log while polling
        this.logger.error(`Local preview rendering: ${message}`)
        this.$errorMessage.set(message)
    }

    private setErrorFromException(context: string, err: unknown) {
        // if the connection has already been lost, the connection error message is the more useful one
        if (this.state === "Disconnected" || this.disconnectRequested) return
        const details = err instanceof Error ? err.message : String(err)
        this.$errorMessage.set(`${context}: ${details}`)
    }

    private incrementPendingTasks() {
        this.$_numPendingTasks.set(this.$_numPendingTasks() + 1)
    }

    private decrementPendingTasks() {
        this.$_numPendingTasks.set(this.$_numPendingTasks() - 1)
    }

    async clearCaches() {
        this.dataObjectSendCache.clear()
        this.tileUploadState.clear()
        await this.$renderer()?.clearCaches()
    }

    private async fetchDataObject(dataObjectLegacyId: LegacyId): Promise<RenderingDataObjectFragment> {
        return this.dataObjectCache.getOrResolve(dataObjectLegacyId, async (dataObjectLegacyId) => {
            this.incrementPendingTasks()
            const dataObject = (await fetchThrowingErrors(this.getDataObjectDetails)({legacyId: dataObjectLegacyId})).dataObject
            this.decrementPendingTasks()
            return dataObject
        })
    }

    private async fetchDataObjectData(dataObjectLegacyId: LegacyId) {
        const dataObject = await this.fetchDataObject(dataObjectLegacyId)
        const resp = await fetchOk(dataObject.downloadUrl)
        return {
            buffer: await resp.arrayBuffer(),
            contentType: resolveDataObjectContentType(dataObject),
        }
    }

    private async loadDataObjects(dataObjectLegacyIds: LegacyId[]): Promise<RenderingDataObjectFragment[]> {
        return Promise.all(dataObjectLegacyIds.map((legacyId) => this.fetchDataObject(legacyId)))
    }

    private async sendDataObject(dataObjectLegacyId: LegacyId): Promise<void> {
        // cached per renderer session, so repeat renders (a camera move re-submits the whole graph)
        // neither re-download the data object nor push it over the socket again
        await this.dataObjectSendCache.getOrResolve(dataObjectLegacyId, async (legacyId) => {
            this.incrementPendingTasks()
            try {
                const dataObject = await this.fetchDataObjectData(legacyId)
                const renderer = this.$renderer()
                if (!renderer) throw Error("No renderer session")
                await renderer.cacheEntity({
                    type: "dataObject",
                    dataObjectId: legacyId,
                    data: new Uint8Array(dataObject.buffer),
                    contentType: dataObject.contentType,
                })
            } finally {
                this.decrementPendingTasks()
            }
        })
    }

    private checkForPendingUpdateOrReturnToIdle() {
        // note: if the socket was closed while we were fetching data, the state will already be set to "Disconnected"
        if (this.state === "Disconnected") return
        this.changeState("Idle")
        if (this.updatePending) {
            this.updatePending = false
            this.update()
        }
    }

    private onUpdateTimer() {
        if (this.$enable()) {
            const renderer = this.$renderer()
            switch (this.state) {
                case "Disconnected":
                    if (!this.lastConnectionAttempt || new Date().getTime() - this.lastConnectionAttempt.getTime() > CONNECT_INTERVAL_MS) {
                        this.connect()
                    }
                    break
                case "Idle":
                    if (renderer) {
                        const nodes = this.$sceneNodes()
                        const config = this.$config()
                        if (
                            nodes &&
                            config &&
                            (nodes !== this.lastSubmittedNodes ||
                                this.$cameraOverride() !== this.lastSubmittedCamera ||
                                JSON.stringify(config) !== this.lastSubmittedConfigKey)
                        ) {
                            this.update()
                            break
                        }
                        this.changeState("FetchingStatus")
                        renderer
                            .getStatus()
                            .then((status) => {
                                this.$statusMessage.set(status.message)
                                this.$progress.set(status.progress)
                                if (status.state === "error") {
                                    this.setError(`Render engine error: ${status.message}`)
                                } else if (status.progress > 0) {
                                    this.$errorMessage.set(null)
                                }
                                if (status.progress !== this.lastProgress) {
                                    this.lastProgress = status.progress
                                    if (status.progress > 0 && !this.updatePending) {
                                        this.changeState("FetchingPreview")
                                        renderer
                                            .getPreviewImage("raw")
                                            .then((image) => {
                                                if (image && !this.updatePending) {
                                                    this.$previewImageData.set(rendererImageToTypedImageData(image))
                                                } else {
                                                    this.$previewImageData.set(null)
                                                }
                                                this.checkForPendingUpdateOrReturnToIdle()
                                            })
                                            .catch((err) => {
                                                this.logger.error("Failed to get preview image", err)
                                                this.setErrorFromException("Failed to get preview image", err)
                                                this.checkForPendingUpdateOrReturnToIdle()
                                            })
                                    } else {
                                        this.checkForPendingUpdateOrReturnToIdle()
                                    }
                                } else {
                                    this.checkForPendingUpdateOrReturnToIdle()
                                }
                            })
                            .catch((err) => {
                                this.logger.error("Failed to get status", err)
                                this.setErrorFromException("Failed to get render status", err)
                                this.checkForPendingUpdateOrReturnToIdle()
                            })
                    }
                    break
                default:
                    break
            }
        } else {
            switch (this.state) {
                case "Disconnected":
                case "Disconnecting":
                    break
                default:
                    this.disconnect()
                    break
            }
        }
    }

    private async overrideSceneNodes(nodes: SceneNodes.SceneNode[], config: PreviewConfig, camera?: SceneNodes.Camera): Promise<SceneNodes.SceneNode[]> {
        const textureStreaming =
            config.textureStreaming === "on"
                ? true
                : config.textureStreaming === "off"
                  ? false
                  : (nodes.find(SceneNodes.RenderSettings.is)?.textureStreaming ?? true) // "from-render-node": the real node's already-resolved value

        const renderSettings: SceneNodes.RenderSettings = {
            type: "RenderSettings",
            templateDepth: 0,
            id: "previewRenderSettings",
            path: [],
            samples: config.samples,
            gpu: config.useGPU,
            cloud: false,
            percentage: 100,
            textureStreaming,
            renderTiling: "off",
            denoiseTiling: "auto",
        }
        const newNodes: SceneNodes.SceneNode[] = [renderSettings]

        //Preview should always have exposure 1, as post-processing effects are applied in the viewer in realtime
        const exposureOneCamera = (camera: SceneNodes.Camera): SceneNodes.Camera => {
            const {exposure: _exposure, ...rest} = camera
            return {
                ...rest,
                resolutionX: config.width,
                resolutionY: config.height,
                exposure: 1,
            }
        }

        let haveCamera = false
        if (camera) {
            newNodes.push(exposureOneCamera(camera))
            haveCamera = true
        }

        for (const node of nodes) {
            if (SceneNodes.RenderSettings.is(node)) {
                continue
            } else if (SceneNodes.Camera.is(node)) {
                if (haveCamera) continue
                newNodes.push(exposureOneCamera(node))
                haveCamera = true
            } else {
                newNodes.push(node)
            }
        }

        if (
            this.$ambientLight() &&
            !nodes.some(
                (n) => ((SceneNodes.AreaLight.is(n) || SceneNodes.PointLight.is(n) || SceneNodes.SunLight.is(n)) && n.on) || SceneNodes.Environment.is(n),
            )
        ) {
            const defaultEnvMap: SceneNodes.Environment = {
                id: "defaultEnvironment",
                type: "Environment",
                envData: {
                    type: "dataObject",
                    dataObject: await this.dataResolver.resolve.hdriObjectLegacy(13), //Scene Editor Envmap (low-res)
                },
                intensity: 1,
                rotation: new Vector3(0, 0, 0),
                mirror: false,
                saturation: 100,
                path: [],
            }
            newNodes.push(defaultEnvMap)
        }

        return newNodes
    }

    private overrideRenderNodes(root: RenderNodes.Render, config: PreviewConfig): RenderNodes.Render {
        return {
            ...root,
            width: Math.round(config.width),
            height: Math.round(config.height),
            samples: Math.round(config.samples),
            session: {
                ...root.session,
                options: {
                    ...root.session.options,
                    gpu: config.useGPU,
                    passes: [config.pass ?? "Combined"],
                    preview_pass: config.pass,
                },
            },
        }
    }

    materialCache = new Map<string, Promise<MaterialGraphCyclesReturns>>()

    private async update() {
        this.$previewImageData.set(null)
        if (this.state !== "Idle") {
            this.updatePending = true
            return
        }
        try {
            const nodes = this.$sceneNodes()
            const config = this.$config()
            const camera = this.$cameraOverride()
            const renderer = this.$renderer()
            if (!(renderer && nodes && config)) {
                return
            }
            this.changeState("Building")

            const imageDataObjectsById = new Map<string, ImageDataObjectInfo>()
            const registerImageDataObject = (dataObjectWithRelated: DataObjectWithRelated, textureSetRevisionId?: string) => {
                const existing = imageDataObjectsById.get(dataObjectWithRelated.id)
                if (existing) {
                    if (textureSetRevisionId !== undefined) existing.associatedTextureSetRevisionIds.push(textureSetRevisionId)
                } else {
                    imageDataObjectsById.set(dataObjectWithRelated.id, {
                        dataObjectWithRelated,
                        associatedTextureSetRevisionIds: textureSetRevisionId !== undefined ? [textureSetRevisionId] : [],
                    })
                }
            }

            // make sure to disable schema verification, as this significantly slows down updates
            const mainRenderGraph = this.overrideRenderNodes(
                await buildRenderGraph(
                    {
                        nodes: await this.overrideSceneNodes(nodes, config, camera),
                        fullQuality: config.fullQuality,
                        passes: ["Combined"],
                        renderPurpose: "interactive",
                        verifySchema: false,
                        materialCache: this.materialCache,
                        registerImageDataObject,
                        textureResolution: config.textureResolution,
                    },
                    this.dataResolver.resolve,
                    this.logger,
                ),
                config,
            )
            const graphHash = computeGraphHash(mainRenderGraph)
            if (graphHash !== this.lastGraphHash || graphHash === undefined) {
                this.lastGraphHash = graphHash
                this.changeState("Updating")
                this.lastProgress = undefined
                this.lastSubmittedNodes = nodes
                this.lastSubmittedCamera = camera
                this.lastSubmittedConfigKey = JSON.stringify(config)

                // Make sure every referenced texture actually has tiles generated.
                if (mainRenderGraph.session.options.texture_streaming) {
                    void this.imageDataObjectThumbnailTrigger.triggerUpdateJobs(Array.from(imageDataObjectsById.values()), {
                        legacyOnly: true,
                        skipFailed: true,
                    })
                }

                await renderWithTiles(
                    renderer,
                    mainRenderGraph,
                    {
                        getDataObjectData: (dataObjectId) => this.fetchDataObjectData(dataObjectId),
                        uploadDataObject: (dataObjectId) => this.sendDataObject(dataObjectId),
                        getImageTilesDescriptor: (dataObjectId) =>
                            this.fetchDataObject(dataObjectId).then((dataObject) => {
                                if (!mainRenderGraph.session.options.texture_streaming) {
                                    return undefined
                                }
                                const descriptor = dataObject.imageTilesDescriptor
                                if (!descriptor) {
                                    return undefined
                                }
                                // the tile pass overwrites this id in the engine with assembled tile data, so a
                                // later untiled render must upload the full object again instead of trusting the cache
                                this.dataObjectSendCache.delete(dataObjectId)
                                return {
                                    ...descriptor,
                                    tileChannels: descriptor.tileChannels ?? 3,
                                }
                            }),
                        decodeExr: (request) => firstValueFrom(this.imageProcessingService.decodeEXR(request)),
                        assembleTiles: (request) => assembleTiles(this.textureTileLoaderService, request),
                    },
                    {
                        statusChanged: (status) => this.$statusMessage.set(status),
                        // tileFeedbackExrAvailable: (blob) => FilesService.downloadFile("tile-feedback.exr", blob),
                    },
                    {
                        logger: this.logger,
                        tileUploadState: this.tileUploadState,
                        textureAssemblyMemoryBudgetBytes: 1024 * 1024 * 1024 * 2, // 2GB
                        maxTextureResolution: resolveTextureResolution(config.textureResolution) ?? 0,
                    },
                )
                this.$errorMessage.set(null)
            }
        } catch (err) {
            this.logger.error("Failed to update render", err)
            this.setErrorFromException("Failed to update render", err)
            this.lastSubmittedCamera = undefined
        }
        this.checkForPendingUpdateOrReturnToIdle()
    }
}
